
import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;

export const supabase = createClient(supabaseUrl, supabaseAnonKey);

// Function to delete user from authentication
export const deleteUserFromAuth = async (userId: string) => {
  try {
    const response = await fetch(`${supabaseUrl}/functions/v1/admin-operations`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${supabaseAnonKey}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ 
        operation: 'delete_auth_user', 
        userId: userId 
      })
    });

    const result = await response.json();

    if (!response.ok) {
      console.error('Error deleting auth user:', result);
      return { success: false, error: result };
    }

    return { success: true, data: result };
  } catch (error) {
    console.error('Error in deleteUserFromAuth:', error);
    return { success: false, error };
  }
};

// Función para operaciones de administración usando Edge Function
export const adminOperation = async (operation: string, table: string, data?: any, id?: string) => {
  try {
    const response = await fetch(`${supabaseUrl}/functions/v1/admin-operations`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${supabaseAnonKey}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ operation, table, data, id })
    });

    const result = await response.json();

    if (!response.ok) {
      console.error('Error en operación admin:', result);
      return { data: null, error: result };
    }

    return { data: result.data, error: null };
  } catch (error) {
    console.error('Error en adminOperation:', error);
    return { data: null, error };
  }
};

// Función para subir imagen usando Edge Function
export const uploadImage = async (file: File, folder: string = 'general'): Promise<string | null> => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('folder', folder);

    const response = await fetch(`${supabaseUrl}/functions/v1/upload-image`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${supabaseAnonKey}`,
      },
      body: formData
    });

    const result = await response.json();

    if (!response.ok) {
      console.error('Error subiendo imagen:', result);
      return null;
    }

    return result.url;
  } catch (error) {
    console.error('Error en uploadImage:', error);
    return null;
  }
};

// Función para eliminar imagen usando Edge Function
export const deleteImage = async (imageUrl: string): Promise<boolean> => {
  try {
    // Validar que la URL no esté vacía
    if (!imageUrl || typeof imageUrl !== 'string') {
      console.warn('URL de imagen inválida o vacía:', imageUrl);
      return true; // Retornar true porque no es un error crítico
    }

    // Solo intentar eliminar si es una imagen de Supabase
    if (!imageUrl.includes('supabase') && !imageUrl.includes('/storage/v1/object/public/')) {
      console.warn('La imagen no es de Supabase, saltando eliminación:', imageUrl);
      return true; // Retornar true porque no es un error
    }

    // Verificar que las variables de entorno estén disponibles
    if (!supabaseUrl || !supabaseAnonKey) {
      console.error('Variables de entorno de Supabase no configuradas');
      return true; // No bloquear la eliminación del registro
    }

    const response = await fetch(`${supabaseUrl}/functions/v1/upload-image`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${supabaseAnonKey}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ imageUrl })
    });

    if (!response.ok) {
      const result = await response.json().catch(() => ({ error: 'Error desconocido' }));
      console.error('Error eliminando imagen:', result);
      return true; // No bloquear la eliminación del registro
    }

    const result = await response.json();
    return result.success || true; // Cambiar a true por defecto
  } catch (error) {
    console.warn('Error en deleteImage (no crítico):', (error as Error)?.message || error);
    // Si hay error de red o conexión, no fallar silenciosamente
    // pero tampoco bloquear la eliminación del registro
    return true; // Cambiar a true para no bloquear
  }
};

// Tipos de datos
export interface User {
  id: string;
  email: string;
  username: string;
  shelter_name: string;
  city: string;
  state: string;
  phone: string;
  plan: string;
  created_at: string;
}

export interface Shelter {
  id: string;
  user_id: string;
  name: string;
  location: string;
  description: string;
  image_url?: string;
  image_url_2?: string;
  image_url_3?: string;
  image_url_4?: string;
  capacity: number;
  current_dogs: number;
  contact: string;
  contact_phone?: string;
  whatsapp_link?: string;
  facebook_link?: string;
  instagram_link?: string;
  donation_bank_info?: string;
  active: boolean;
  created_at: string;
  updated_at: string;
}

export interface DonationCampaign {
  id: string;
  user_id: string;
  title: string;
  description: string;
  image_url?: string;
  image_url_2?: string;
  image_url_3?: string;
  image_url_4?: string;
  goal: number;
  raised: number;
  urgent: boolean;
  active: boolean;
  bank_info?: string;
  payment_button1_name?: string;
  payment_button1_url?: string;
  payment_button1_enabled?: boolean;
  payment_button2_name?: string;
  payment_button2_url?: string;
  payment_button2_enabled?: boolean;
  contact_phone?: string;
  whatsapp_link?: string;
  created_at: string;
  updated_at: string;
}

export interface AdoptionCase {
  id: string;
  user_id: string;
  name: string;
  age: string;
  breed: string;
  description: string;
  image_url?: string;
  image_url_2?: string;
  image_url_3?: string;
  image_url_4?: string;
  location: string;
  vaccinated: boolean;
  sterilized: boolean;
  adopted: boolean;
  whatsapp_phone?: string;
  contact_phone?: string;
  requirement_1?: string;
  requirement_2?: string;
  requirement_3?: string;
  requirement_4?: string;
  requirement_5?: string;
  created_at: string;
  updated_at: string;
}

export interface AdoptionRequest {
  id: string;
  adoption_case_id: string;
  user_id: string;
  applicant_name: string;
  applicant_city: string;
  applicant_phone: string;
  applicant_occupation: string;
  dog_name: string;
  status: string;
  created_at: string;
  updated_at: string;
}

export interface ShelterEvent {
  id: string;
  user_id: string;
  shelter_id: string;
  title: string;
  description: string;
  image_url_1?: string;
  image_url_2?: string;
  image_url_3?: string;
  image_url_4?: string;
  image_url_5?: string;
  event_date: string;
  location: string;
  address: string;
  attendees_count: number;
  active: boolean;
  created_at: string;
  updated_at: string;
}

export interface EventAttendee {
  id: string;
  event_id: string;
  attendee_name: string;
  attendee_phone?: string;
  attendee_email?: string;
  created_at: string;
}

// New product interface added
export interface Product {
  id: string;
  name: string;
  description?: string;
  price: number;
  image_url?: string;
  image_url_2?: string;
  image_url_3?: string;
  image_url_4?: string;
  active: boolean;
  created_at: string;
  updated_at: string;
}

// Exportaciones adicionales para compatibilidad
export type UserType = User;
export type ShelterType = Shelter;
export type DonationCampaignType = DonationCampaign;
export type AdoptionCaseType = AdoptionCase;
export type AdoptionRequestType = AdoptionRequest;
export type ShelterEventType = ShelterEvent;
export type EventAttendeeType = EventAttendee;
export type ProductType = Product;
